/**
 * Helpers for surfacing WebAuthn "roaming" signals in the UI.
 *
 * A synced passkey (iCloud Keychain / Google Password Manager) is one
 * credential usable from several physical devices, so it bypasses per-device
 * enrollment. The backend exposes this via the WebAuthn backup flags
 * (backup_eligible = BE, backed_up = BS). All fields are optional here — if the
 * backend hasn't added them yet, these degrade to "unknown" and render nothing.
 */

/** True when a credential can sync across devices (backup-eligible passkey). */
export function isMultiDevice(cred) {
  if (!cred) return false;
  return !!(cred.backup_eligible ?? cred.backed_up ?? cred.multi_device);
}

/**
 * Compact, human label from a stored user-agent STRING (not the live browser —
 * react-device-detect can't parse arbitrary strings). Best-effort; falls back
 * to a truncation so we never show a wall of UA text.
 */
export function shortUserAgent(ua) {
  if (!ua || typeof ua !== "string") return "";

  const browser =
    /edg\//i.test(ua) ? "Edge" :
    /opr\/|opera/i.test(ua) ? "Opera" :
    /chrome|crios/i.test(ua) ? "Chrome" :
    /firefox|fxios/i.test(ua) ? "Firefox" :
    /safari/i.test(ua) ? "Safari" :
    "";

  const os =
    /iphone|ipad|ipod/i.test(ua) ? "iOS" :
    /android/i.test(ua) ? "Android" :
    /mac os x|macintosh/i.test(ua) ? "macOS" :
    /windows/i.test(ua) ? "Windows" :
    /linux/i.test(ua) ? "Linux" :
    "";

  const parts = [browser, os].filter(Boolean);
  if (parts.length) return parts.join(" · ");
  return ua.length > 40 ? `${ua.slice(0, 40)}…` : ua;
}
